import React, {useState} from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  FormControl,
  Text,
  FormLabel,
  GridItem,
  VStack,
  Grid,
  Box,
} from '@chakra-ui/react';
import {useFormik} from 'formik';
import {useStatusToast} from "../../../../hooks/useStatusToast";
import {createNotionDatabase} from "../../../../services/api";
import {CreateNotionDatabaseInput} from "@app/core-app/app/data/input/create-notion-database.input";
import {useAvailablePages} from "../../../../data/pages";
import {event} from "../../../../services/analytics";
import ParentPageComponent from "./ParentPageComponent";


const DatabaseTypeCard = ({type, description, isChecked, onChange, value}: any) => {
  return (
    <GridItem
      as="label"
      role="group"
      cursor="pointer"
      borderWidth="1px"
      borderRadius="md"
      boxShadow="md"
      py={4}
      px={6}
      bg={isChecked ? 'blue.400' : 'white'}
      color={isChecked ? 'white' : 'gray.800'}
      _hover={isChecked ? {borderColor: 'blue.600', bg: 'blue.600'} : {borderColor: 'blue.600', bg: 'blue.100'}}
      onClick={() => onChange(value)}
    >
      <Text fontWeight="bold" mb={2}>
        {type}
      </Text>
      <Text fontSize="sm">{description}</Text>
    </GridItem>
  );
};


const NotionDatabaseForm = ({onComplete}: { onComplete: (name: string) => void }) => {

  const {data: pages, isLoading} = useAvailablePages()

  const {toastCatch} = useStatusToast()

  const [progress, setProgress] = useState(false);

  const formik = useFormik<CreateNotionDatabaseInput>({
    initialValues: {
      databaseName: '',
      databaseParent: pages ? pages[0]?.id : "",
      databaseType: 'inbox',
      inboxConfiguration: 'plain_text',
      outboxRecipientsMode: false
    },
    onSubmit: (values) => {
      if (values.databaseName && values.databaseParent) {
        setProgress(true)
        return createNotionDatabase(values)
          .then(r => {
            if (!r.data) {
              throw new Error("Bad Result")
            }
            event({action: "create_database", params: {type: values.databaseType}})
            onComplete(values.databaseName)
          })
          .catch(toastCatch)
      }
    },
  });

  const [selectedType, setSelectedType] = React.useState("inbox");

  return (
    <form onSubmit={formik.handleSubmit}>
      <VStack spacing={4}>
        <FormControl>
          <FormLabel>Database name</FormLabel>
          <Input
            name="databaseName"
            onChange={formik.handleChange}
            value={formik.values.databaseName}
          />
        </FormControl>

        <ParentPageComponent formik={formik} isLoading={isLoading} pages={pages}/>

        <FormControl>
          <FormLabel>Feature</FormLabel>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <DatabaseTypeCard
              type="Inbox"
              value={"inbox"}
              description="Save emails in Notion. NotionSender will create a database with columns for saving emails."
              isChecked={formik.values.databaseType === 'inbox'}
              onChange={(type: string) => {
                formik.setFieldValue('databaseType', type);
                setSelectedType(type);
              }}
            />

            <DatabaseTypeCard
              type="Outbox"
              value={"outbox"}
              description="Send emails from Notion. NotionSender will create a database with columns for sending emails."
              isChecked={formik.values.databaseType === 'outbox'}
              onChange={(type: string) => {
                formik.setFieldValue('databaseType', type);
                setSelectedType(type);
              }}
            />

          </Grid>
        </FormControl>

        {selectedType === 'inbox' && (
          <FormControl>
            <FormLabel>Inbox Configuration</FormLabel>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <DatabaseTypeCard
                type="Save as Notion blocks"
                value={"plain_text"}
                description="Convert email to the Notion blocks. Works best for the regular emails."
                isChecked={formik.values.inboxConfiguration === 'plain_text'}
                onChange={(type: string) => formik.setFieldValue('inboxConfiguration', type)}
              />
              <DatabaseTypeCard
                type="Save as widget"
                value={"embed"}
                description="Embed interactive widget inside Notion page. Works best for newsletters."
                isChecked={formik.values.inboxConfiguration === 'embed'}
                onChange={(type: string) => formik.setFieldValue('inboxConfiguration', type)}
              />
            </Grid>
          </FormControl>
        )}

        {selectedType === 'outbox' && (
          <FormControl>
            <FormLabel>Outbox Recipients Configuration</FormLabel>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <DatabaseTypeCard
                type="Recipients from page property"
                description="You will specify recipients list in page property of your email page."
                isChecked={!formik.values.outboxRecipientsMode}
                onChange={() => formik.setFieldValue('outboxRecipientsMode', false)}
              />
              <DatabaseTypeCard
                type="Recipients from separate database"
                description="NotionSender will create a separate database for recipients. Each row of the database is a recipient for email."
                isChecked={formik.values.outboxRecipientsMode}
                onChange={() => formik.setFieldValue('outboxRecipientsMode', true)}
              />
            </Grid>
          </FormControl>
        )}

        <Box my={8} w={'full'}>
          <Box my={2} color={"gray.500"} fontSize={"sm"} alignItems={"start"}>
                        It may take a few minutes before created database will appear in the Notion Databases list.
          </Box>
          <Button type="submit" colorScheme="blue" w={'full'} isLoading={progress}>
            <span>Create database</span>
          </Button>
        </Box>
      </VStack>
    </form>
  );
};

function CreateFormModal({isOpen, onClose}: any) {

  const {addToast} = useStatusToast()

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Create new notion database</ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            <NotionDatabaseForm
              onComplete={(name) => {
                onClose();
                addToast({
                  title: "Database creation started!",
                  description: `"${name}" will show up in the list within 2–3 minutes. Hit the refresh button next to 'Notion Databases' to check.`,
                  status: "info",
                  duration: 8000,
                  isClosable: true,
                });
              }}/>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export function CreateFormButton() {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenModal = () => {
    event({action: "open_create_database_modal", params: {}})
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button size="sm"
        w={{base: "100%", md: "auto"}}
        colorScheme="blue" onClick={handleOpenModal}>
        <span>Create new email database</span>
      </Button>
      <CreateFormModal isOpen={isOpen} onClose={handleCloseModal}/>
    </>
  );
}
